import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	FetchCancellationReasons200Item as CancelReason,
	FetchCancellationReasonsStatus as CancelShipmentStatus,
	type DeleteGroupByIdParams,
	useDeleteGroupById,
	useFetchCancellationReasons,
} from '@uturn/api/v1';
import {
	type CancelShipmentsParams,
	GetShipmentsFilterStatusesItem as ShipmentFilterStatus,
	useCancelShipments,
} from '@uturn/api/v2';
import { useFacetSearch } from '@uturn/portal/hooks/queries/meilisearch';
import { ShipmentGroupDetailContext } from '@uturn/portal/modules/shipments/providers/shipment-group-detail';
import { useShipmentsSearchStore } from '@uturn/portal/store/shipments/shipments-search';
import { z } from '@uturn/portal/zod';
import { addIssueRequired } from '@uturn/portal/zod/utils';
import {
	Button,
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DropdownMenuItem,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	sonner,
	Textarea,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@uturn/ui-kit';
import { useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type CancelShipmentsException, exceptions } from './exceptions';

const DEFAULT_CANCEL_SHIPMENTS_STATUS = CancelShipmentStatus.PUBLISHED;

// Statuses the backend won't cancel in a bulk request — exposed so the
// layouts that drive the modal can subtract them from totals and the submit
// payload can strip them out.
export const NON_CANCELLABLE_BULK_STATUSES: ShipmentFilterStatus[] = [
	ShipmentFilterStatus.EXECUTED,
	ShipmentFilterStatus.COST_DECLARED,
];

function getCancelShipmentStatus(
	statuses: ShipmentFilterStatus[],
	groupId?: number,
): CancelShipmentStatus | undefined {
	if (!statuses.length) {
		return groupId ? DEFAULT_CANCEL_SHIPMENTS_STATUS : undefined;
	}
	if (statuses.includes(ShipmentFilterStatus.CONCEPT)) {
		return CancelShipmentStatus.CONCEPT;
	}
	if (statuses.includes(ShipmentFilterStatus.PUBLISHED)) {
		return CancelShipmentStatus.PUBLISHED;
	}
	if (statuses.includes(ShipmentFilterStatus.CARRIER_ASSIGNED)) {
		return CancelShipmentStatus.CARRIER_ASSIGNED;
	}
	return undefined;
}

export function ShipmentsCancelDropdownMenuItem({
	setOpenModal,
	cancelShipmentsException,
	disabled,
	disabledTooltip,
}: {
	setOpenModal: (open: boolean) => void;
	cancelShipmentsException?: CancelShipmentsException;
	disabled?: boolean;
	disabledTooltip?: string;
}) {
	const { t } = useTranslation();
	const onSelect = () => {
		if (cancelShipmentsException) {
			const exception = exceptions(t)[cancelShipmentsException];
			sonner.error(exception.description);
			return;
		}
		setOpenModal(true);
	};

	const item = (
		<DropdownMenuItem disabled={disabled} onSelect={onSelect}>
			{t('pages.shippers.actions.cancel', 'Cancel')}
		</DropdownMenuItem>
	);

	if (disabled && disabledTooltip) {
		return (
			<TooltipProvider delayDuration={10}>
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="block">{item}</span>
					</TooltipTrigger>
					<TooltipContent side="left">{disabledTooltip}</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}

	return item;
}

export function ShipmentsCancelModal({
	exportFilters,
	statuses,
	groupId,
	isOpenModal,
	setOpenModal,
	totalGroupShipments,
	nonCancellableCount,
}: {
	exportFilters: CancelShipmentsParams | DeleteGroupByIdParams;
	statuses: ShipmentFilterStatus[];
	groupId?: number;
	isOpenModal: boolean;
	setOpenModal: (open: boolean) => void;
	totalGroupShipments?: number;
	nonCancellableCount?: number;
}) {
	const { t } = useTranslation();
	const { totalShipments } = useShipmentsSearchStore();
	const { groupId: contextGroupId } = useContext(ShipmentGroupDetailContext);

	const statusFilter =
		statuses.length > 0 ? [`shipmentStatus IN [${statuses.join(',')}]`] : [];
	const facetSearch = useFacetSearch(
		'shipmentStatus',
		contextGroupId,
		statusFilter,
	);
	const facetNonCancellableCount = facetSearch.data?.facetHits
		.filter((hit) =>
			NON_CANCELLABLE_BULK_STATUSES.includes(hit.value as ShipmentFilterStatus),
		)
		.reduce((sum, hit) => sum + hit.count, 0);
	const effectiveNonCancellableCount =
		nonCancellableCount ?? facetNonCancellableCount ?? 0;

	const cancelShipmentStatus = useMemo(
		() => getCancelShipmentStatus(statuses, groupId),
		[statuses, groupId],
	);

	const { data: cancelReasons } = useFetchCancellationReasons(
		{
			status: cancelShipmentStatus ?? DEFAULT_CANCEL_SHIPMENTS_STATUS,
			isMultiCancel: true,
			isGroupCancel: !!groupId,
		},
		{ query: { enabled: !!cancelShipmentStatus, select: (data) => data.data } },
	);

	// totalGroupShipments is already the cancellable-only count from the per-group
	// card path. For the page-level path, totalShipments is the raw filter total,
	// so subtract executed to get the cancellable-only count.
	const totalCancelShipments =
		totalGroupShipments ??
		Math.max(totalShipments - effectiveNonCancellableCount, 0);

	const formSchema = z
		.object({
			reasonSelect: z.string().min(1, {
				message: t(
					'general.errors.form_validation.select_required',
					'Must select a {{propertyName}}',
					{ propertyName: 'reason' },
				).toString(),
			}),
			reasonOtherExplanation: z
				.string()
				.max(255, {
					message: t(
						'general.errors.form_validation.max',
						'{{propertyName}} must be less than {{maxLength}} characters',
						{ propertyName: 'Reason', maxLength: 255 },
					).toString(),
				})
				.optional(),
		})
		.superRefine((values, ctx) => {
			if (
				values.reasonSelect === CancelReason.OTHER &&
				!values.reasonOtherExplanation
			) {
				addIssueRequired(ctx, 'reasonOtherExplanation');
			}
		});

	type FormValues = z.infer<typeof formSchema>;

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		mode: 'onChange',
		defaultValues: {
			reasonSelect: '',
			reasonOtherExplanation: '',
		},
	});

	const watchReasonSelect = form.watch('reasonSelect');

	const mutation = {
		onSuccess: () => {
			sonner.success(
				t(
					'pages.shipments_cancel.dialog.form.submit.success',
					'Shipments are being cancelled. Updates may take a few minutes. Please refresh.',
				),
			);
			form.reset();
			setOpenModal(false);
		},
		onError: () => {
			sonner.error(
				t(
					'pages.shipments_cancel.dialog.form.submit.error',
					'Could not submit shipments for cancellation.',
				),
			);
			setOpenModal(false);
		},
	};

	const {
		mutate: cancelShipmentGroupById,
		isPending: cancellingShipmentGroup,
	} = useDeleteGroupById({ mutation });

	const { mutate: cancelShipments, isPending: cancellingShipments } =
		useCancelShipments({ mutation });

	const cancelling = cancellingShipmentGroup || cancellingShipments;

	const onSubmit = async (data: FormValues) => {
		if (!totalCancelShipments) {
			sonner.warning(
				t(
					'pages.shipments_cancel.dialog.form.errors.deny_no_shipments',
					'No shipments selected. Nothing to cancel.',
				),
			);
			return;
		}

		const reason =
			data.reasonSelect === CancelReason.OTHER
				? data.reasonOtherExplanation
				: t(
						`general.shipments.cancel-reason.${data.reasonSelect}`,
						data.reasonSelect,
					);

		if (!reason) {
			sonner.error(
				t(
					'pages.shipments_cancel.dialog.form.errors.reason_not_found',
					'Could not find submitted reason',
				),
			);
			return;
		}

		groupId
			? cancelShipmentGroupById({
					id: groupId,
					data: reason,
					params: exportFilters,
				})
			: cancelShipments({
					data: reason,
					params: {
						...exportFilters,
						'filter.statuses': exportFilters['filter.statuses']?.filter(
							(filter) => !NON_CANCELLABLE_BULK_STATUSES.includes(filter),
						),
					},
				});
	};

	if (!cancelShipmentStatus) {
		if (isOpenModal) {
			sonner.error(
				t('general.errors.generic_title', 'Uh oh! Something went wrong.'),
			);
			setOpenModal(false);
		}
		return <></>;
	}

	return (
		<Dialog
			open={isOpenModal}
			onOpenChange={(open) => {
				if (!open) {
					form.reset();
				}
				setOpenModal(open);
			}}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{effectiveNonCancellableCount > 0
							? t(
									'pages.shipments_cancel.dialog.heading-with-skipped',
									'Cancel {{totalCancelShipments}} of {{grandTotal}} shipments',
									{
										totalCancelShipments,
										grandTotal:
											totalCancelShipments + effectiveNonCancellableCount,
									},
								)
							: t(
									'pages.shipments_cancel.dialog.heading',
									'Cancel {{totalCancelShipments}} shipments',
									{
										totalCancelShipments,
									},
								)}
					</DialogTitle>
					<DialogDescription>
						{groupId
							? t(
									'pages.grouped_shipments_cancel.dialog.description',
									'You are about to cancel all the shipments from this group.',
								)
							: t(
									'pages.shipments_cancel.dialog.description',
									'Change the shipment filters to modify the selection to cancel.',
								)}
						{effectiveNonCancellableCount > 0 && (
							<p>
								{t(
									'pages.shipments_cancel.dialog.non-cancellable-skipped',
									'{{nonCancellableCount}} executed or cost-declared shipments cannot be cancelled and will be skipped.',
									{ nonCancellableCount: effectiveNonCancellableCount },
								)}
							</p>
						)}
						{totalGroupShipments && effectiveNonCancellableCount === 0 && (
							<p>
								{t(
									'pages.shipments_cancel.dialog.group-note',
									'Note: Any archived shipments in this group will be ignored.',
								)}
							</p>
						)}
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="reasonSelect"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t(
											'pages.shipments_cancel.dialog.form.reason.label',
											'Why do you want to cancel the current shipment selection?',
										)}
									</FormLabel>
									<Select
										{...field}
										onValueChange={field.onChange}
										disabled={cancelling}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue
													placeholder={
														<span>
															{t(
																'pages.shipments_cancel.dialog.form.reason.placeholder',
																'Select a reason',
															)}
														</span>
													}
												/>
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{cancelReasons?.map(
												(reasonCode: CancelReason, index: number) => (
													<SelectItem key={index} value={reasonCode}>
														{t(
															`general.shipments.cancel-reason.${reasonCode}`,
															reasonCode as string,
														)}
													</SelectItem>
												),
											)}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
						{watchReasonSelect === CancelReason.OTHER && (
							<FormField
								control={form.control}
								name="reasonOtherExplanation"
								render={({ field }) => (
									<FormItem>
										<FormControl>
											<Textarea
												{...field}
												onChange={field.onChange}
												disabled={cancelling}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}
						<DialogFooter>
							<DialogClose asChild>
								<Button
									type="button"
									disabled={cancelling}
									variant="outline"
								>
									{t('pages.shipments_cancel.dialog.button.cancel', 'Cancel')}
								</Button>
							</DialogClose>
							<Button
								type="submit"
								disabled={cancelling || !form.formState.isValid}
							>
								{cancelling ? (
									<>
										<Icon className="mr-2 size-4" icon={faSpinnerThird} spin />
										{t(
											'pages.shipments_cancel.dialog.button.loading',
											'Canceling...',
										)}
									</>
								) : (
									t('pages.shipments_cancel.dialog.button.submit', 'Confirm')
								)}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
